import {
    faArrowLeft,
    faArrowsAltH,
    faCopy,
    faEraser,
    faExclamationTriangle,
    faFileImport,
    faImage,
    faPaste,
    faSave,
    faShareAlt,
    faUndoAlt,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { Button, ButtonGroup, Divider, Input, InputGroup, Message, Panel, SelectPicker } from 'rsuite';
import { FontValueInterface } from '..';
import { IpCan } from '../../../handlers/ipcan/IpCan';
import { LPDisplay } from '../../../handlers/lpDisplays/LPDisplay';
import { PixelArtFontImport } from '../../../handlers/pixelArt/pixelArtWS';
import PanelHeader from '../../Custom/PanelHeader';
import { ImportFontModal } from './ImportFontModal';
import { ResetFontModal } from './ResetFontModal';
import { SaveFontModal } from './SaveFontModal';
import { SendFontToManyDisplayModal } from './SendFontToManyDisplayModal';
import { downloadFile, selectFontSizeData } from './utils';

type Props = {
    fontSize: number;
    fonts: FontValueInterface[];
    fontsFromApi: any;
    selectedFontIndex: number;
    isLoading: boolean;
    handleFontWidthChange: (value: number) => void;
    handleFontUpload: (receivedFont: PixelArtFontImport) => void;
    readFontFromAPI: () => void;
    handleSizeChange: (value: number) => void;
    handleSaveFont: () => void;
    handleSaveToManyFont: (displays: number[]) => void;
    handleClickCopy: () => void;
    handleClickPaste: () => void;
    handleClickReset: () => void;
    isPasteButtonEnabled: boolean;
    display?: LPDisplay;
    ipcan?: IpCan;
};

export const RightSideBar = (props: Props) => {
    const intl = useIntl();

    const [importModalIsOpen, setImportModalIsOpen] = React.useState<boolean>(false);

    const [sendToManyModalIsOpen, setSendToManyModalIsOpen] = React.useState<boolean>(false);

    const [isResetModalOpen, setIsResetModalOpen] = React.useState<boolean>(false);

    const [saveModalIsOpen, setSaveModalIsOpen] = React.useState<boolean>(false);

    const currentFont: FontValueInterface | undefined = props.fonts[props.selectedFontIndex];

    const history = useHistory();

    const handleClickSave = () => {
        downloadFile(
            JSON.stringify({
                font: props.fontsFromApi,
                fontSize: props.fontSize,
            }),
            `export-font-${props.fontSize}px-${moment().format('DD_MM_YYYY-HH_mm_ss')}.json`
        );
    };

    const handleClickImport = () => {
        setImportModalIsOpen(true);
    };

    const handleChange = value => {
        props.handleFontWidthChange(value);
    };

    const handleClickReset = () => {
        setIsResetModalOpen(true);
    };

    const handleSizeChange = (value: number) => {
        props.handleSizeChange(value);
    };

    return (
        <>
            <ImportFontModal
                isOpen={importModalIsOpen}
                onHide={() => setImportModalIsOpen(false)}
                handleFontUpload={props.handleFontUpload}
            />

            <ResetFontModal
                isOpen={isResetModalOpen}
                onHide={() => setIsResetModalOpen(false)}
                handleClickReset={() => props.readFontFromAPI()}
            />

            <SendFontToManyDisplayModal
                isOpen={sendToManyModalIsOpen}
                onHide={() => setSendToManyModalIsOpen(false)}
                handleSaveToManyFont={props.handleSaveToManyFont}
            />

            <SaveFontModal
                isOpen={saveModalIsOpen}
                onHide={() => setSaveModalIsOpen(false)}
                handleSaveFont={() => props.handleSaveFont()}
            />

            <Panel
                className="panel-small"
                bordered
                shaded
                header={
                    <PanelHeader
                        title={intl.formatMessage({ id: 'editFont.editorMenu' })}
                        buttons={[
                            <Button
                                color="blue"
                                size="sm"
                                onClick={() => {
                                    if (props.ipcan && props.display) {
                                        return history.push(`/ipCan/${props.ipcan.getId()}/bus/${props.display.bus}`);
                                    } else {
                                        return history.push('/ipCan');
                                    }
                                }}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Button>,
                        ]}
                    />
                }>
                {(props.ipcan || props.display) && (
                    <>
                        <h5 className="text-center">
                            {props.ipcan?.getLabel()} - {props.display?.getBus() && props.display?.getBus()} -{' '}
                            {props.display?.getDeviceId()}
                        </h5>
                        <Divider className="margin-top-10" />
                    </>
                )}
                <ButtonGroup justified>
                    <Button onClick={handleClickReset} color="red" disabled={props.isLoading || !props.display}>
                        <FontAwesomeIcon icon={faUndoAlt} />
                    </Button>
                    <Button color="orange" disabled={props.isLoading} onClick={handleClickImport}>
                        <FontAwesomeIcon icon={faFileImport} />
                    </Button>
                    <Button
                        color="green"
                        disabled={props.isLoading || props.fonts.length === 0}
                        onClick={handleClickSave}>
                        <FontAwesomeIcon icon={faSave} />
                    </Button>
                </ButtonGroup>
                <Divider />
                <ButtonGroup justified>
                    <Button onClick={props.handleClickCopy} color="blue" disabled={props.isLoading}>
                        <FontAwesomeIcon icon={faCopy} />
                    </Button>
                    <Button
                        color="orange"
                        disabled={props.isLoading || !props.isPasteButtonEnabled}
                        onClick={props.handleClickPaste}>
                        <FontAwesomeIcon icon={faPaste} />
                    </Button>
                    <Button color="red" disabled={props.isLoading} onClick={props.handleClickReset}>
                        <FontAwesomeIcon icon={faEraser} />
                    </Button>
                </ButtonGroup>
                <Divider />
                <div className="margin-bottom-15">
                    <p className="bold">
                        <FormattedMessage id="edit.fontSize" />
                    </p>
                    <SelectPicker
                        disabled={props.isLoading}
                        value={props.fontSize}
                        data={selectFontSizeData()}
                        onChange={handleSizeChange}
                        searchable={false}
                        cleanable={false}
                    />
                </div>
                <div>
                    <InputGroup>
                        <InputGroup.Addon>
                            <FontAwesomeIcon icon={faArrowsAltH} />
                        </InputGroup.Addon>
                        <Input
                            type="number"
                            key={`${new Date()}-${props.selectedFontIndex}`}
                            disabled={props.isLoading}
                            defaultValue={currentFont?.value.length.toString() || '0'}
                            step={1}
                            max={100}
                            onChange={value => handleChange(parseInt(value))}
                        />
                    </InputGroup>
                </div>
                <Divider />

                {!props.display && (
                    <Message
                        className="margin-bottom-10"
                        type="warning"
                        description={
                            <>
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    color="orange"
                                    className="margin-right-5"
                                />{' '}
                                <FormattedMessage id="editPicto.noDisplay" />
                            </>
                        }></Message>
                )}

                <ButtonGroup justified>
                    <Button
                        onClick={() => setSaveModalIsOpen(true)}
                        disabled={props.isLoading || !props.display}
                        color="blue">
                        <FontAwesomeIcon icon={faUpload} />
                    </Button>
                    <Button onClick={() => setSendToManyModalIsOpen(true)} disabled={props.isLoading} color="violet">
                        <FontAwesomeIcon icon={faShareAlt} />
                    </Button>
                </ButtonGroup>

                <Divider />
                <Button
                    onClick={() =>
                        history.push(
                            `/ipCan/${props.ipcan?.getId()}/bus/${props.display?.getBus()}/matrix-display/${props.display?.getId()}/picto/1`
                        )
                    }
                    block
                    appearance="ghost">
                    <FontAwesomeIcon icon={faImage} className="margin-right-5" />
                    <FormattedMessage id="editPicto.switchToPicto" />
                </Button>
            </Panel>
        </>
    );
};
